import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';
import DataUploader from '../components/insights/DataUploader';
import QueryInput from '../components/insights/QueryInput';
import ResultsPanel from '../components/insights/ResultsPanel';
import ChartRenderer from '../components/insights/ChartRenderer';
import TaxonomyConfirmation from '../components/insights/TaxonomyConfirmation';
import ReasoningSteps from '../components/insights/ReasoningSteps';
import LogicalThinking from '../components/insights/LogicalThinking';
import DrillDownSuggestion from '../components/insights/DrillDownSuggestion';

const InsightsAtGPT = ({ availableProjects, defaultProject }) => {
  const { userAttributes } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const [wantsToUpload, setWantsToUpload] = useState('No');
  const [uploadedData, setUploadedData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ stage: '', message: '', percentage: 0 });
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [messages, setMessages] = useState([]);
  
  const messagesEndRef = useRef(null);

  // Generate session ID on mount
  useEffect(() => {
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // Set default project from props
  useEffect(() => {
    if (defaultProject && availableProjects && availableProjects.length > 0) {
      setSelectedProject(defaultProject);
    } else if (availableProjects && availableProjects.length > 0) {
      setSelectedProject(availableProjects[0]);
    }
  }, [defaultProject, availableProjects]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleExportCSV = (csvData, queryText) => {
    if (!csvData || csvData.length === 0) {
      alert('No data available to export');
      return;
    }
    
    // Check if multi-intent (array of datasets) or single intent (flat array)
    const isMultiIntent = Array.isArray(csvData) && csvData[0]?.label && csvData[0]?.data;
    
    if (isMultiIntent) {
      // Multi-intent: export each dataset separately
      csvData.forEach((dataset, idx) => {
        if (!dataset.data || dataset.data.length === 0) return;
        
        const csv = Papa.unparse(dataset.data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        const sanitizedQuery = queryText.replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 30);
        const filename = `insights_${sanitizedQuery}_${dataset.label}_${dataset.queryType}_${Date.now()}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log(`✅ CSV exported: ${filename} (${dataset.data.length} rows, ${dataset.queryType})`);
      });
      
      alert(`Exported ${csvData.length} CSV files (one per dataset)`);
    } else {
      // Single intent: export as before
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const sanitizedQuery = queryText.replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 50);
      const filename = `insights_${sanitizedQuery}_${Date.now()}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`✅ CSV exported: ${filename} (${csvData.length} rows)`);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing, progress]);

  const handleProjectChange = (e) => {
    const projectCode = e.target.value;
    setSelectedProject(projectCode);
    setUploadedData(null);
    setWantsToUpload('No');
    setMessages([]);
  };

  const handleDataUpload = (data) => {
    setUploadedData(data);
    setMessages(prev => [...prev, { 
      type: 'system', 
      content: `Data uploaded successfully: ${data.filename || 'Unknown file'}` 
    }]);
  };

  const handleFeedback = async (query, content, feedbackType) => {
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId: 'current-session-id',  // TODO: Get from auth context
          projectCode: selectedProject,
          queryText: query,
          feedbackType: feedbackType,
          diagnosticData: content.diagnosticData || {
            userQuery: query,
            timestamp: new Date().toISOString(),
            metadata: content.metadata
          }
        })
      });

      if (response.ok) {
        // Mark message as feedback submitted
        setMessages(prev => prev.map(msg => 
          msg.query === query ? { ...msg, feedbackSubmitted: true } : msg
        ));
      } else {
        console.error('Failed to submit feedback:', await response.text());
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleQuery = async (query) => {
    if (!selectedProject || !sessionId) return;

    const userMessage = { type: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);

    setIsProcessing(true);
    setProgress({ stage: 'Starting', message: 'Initializing query...', percentage: 0 });
    
    let currentResult = null;

    try {
      // Get token from AWS Amplify session
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch('/api/insights/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          projectCode: selectedProject,
          query,
          options: { advancedMode: isAdvanced }
        }),
      });

      if (!response.ok) {
        throw new Error(`Query failed: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let reasoningSteps = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'progress') {
              setProgress({
                stage: data.stage,
                message: data.message,
                percentage: data.percentage,
              });
            } else if (data.type === 'reasoning') {
              // Accumulate reasoning steps
              if (data.steps) {
                reasoningSteps = [...reasoningSteps, ...data.steps];
              }
            } else if (data.type === 'confirmation') {
              // Handle taxonomy confirmation (confidence 0.70-0.85)
              currentResult = {
                needsConfirmation: true,
                message: data.message,
                alternatives: data.alternatives,
                confidence: data.confidence,
                originalQuery: data.queryText,
                reasoningSteps: reasoningSteps.length > 0 ? reasoningSteps : undefined,
              };
            } else if (data.type === 'result') {
              currentResult = {
                ...data.result,
                reasoningSteps: reasoningSteps.length > 0 ? reasoningSteps : undefined,
              };
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          }
        }
      }

      if (currentResult) {
        setMessages(prev => [...prev, { 
          type: 'ai', 
          content: currentResult,
          query: query
        }]);
      }

    } catch (error) {
      console.error('Query error:', error);
      setMessages(prev => [...prev, { 
        type: 'error', 
        content: { error: error.message, summary: 'Failed to process query. Please try again.' } 
      }]);
    } finally {
      setIsProcessing(false);
      setProgress({ stage: '', message: '', percentage: 0 });
    }
  };

  const handleTaxonomySelection = async (selectedAlternative, originalQuery) => {
    // User selected an alternative - re-run query with confirmed taxonomy
    console.log('User selected:', selectedAlternative);
    
    // Remove the confirmation message
    setMessages(prev => prev.slice(0, -1));
    
    // Serialize the selected hierarchy as JSON for backend to parse
    const confirmedPath = JSON.stringify({
      theme_category: selectedAlternative.theme_category,
      theme: selectedAlternative.theme,
      sub_theme: selectedAlternative.sub_theme,
      theme_classification: selectedAlternative.theme_classification,
      granularity: selectedAlternative.granularity
    });
    
    // Re-submit with confirmed taxonomy (backend will detect and parse)
    await handleQuery(`${originalQuery} [CONFIRMED:${confirmedPath}]`);
  };

  const handleTaxonomyCancel = () => {
    // User cancelled - just remove the confirmation message
    setMessages(prev => prev.slice(0, -1));
  };

  const handleDrillDownSelection = async (theme, categoryName) => {
    // User selected a theme to drill down into
    console.log('User selected drill-down theme:', theme);
    
    // Build conversational query for the selected theme
    const drillDownQuery = `Show me insights for ${theme.label} in ${categoryName}`;
    
    // Submit new query
    await handleQuery(drillDownQuery);
  };

  return (
    <Container>
      <ControlsContainer>
        <ControlGroup>
          <Label>Project Code:</Label>
          <StyledSelect 
            value={selectedProject || ''} 
            onChange={handleProjectChange}
            disabled={!availableProjects || availableProjects.length === 0}
          >
            <option value="">Select Project</option>
            {availableProjects?.map(project => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </StyledSelect>
        </ControlGroup>

        <ControlGroup>
          <Label>Upload Screener Data?</Label>
          <StyledSelect 
            value={wantsToUpload} 
            onChange={(e) => setWantsToUpload(e.target.value)}
            disabled={!selectedProject}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </StyledSelect>
        </ControlGroup>
      </ControlsContainer>

      {wantsToUpload === 'Yes' && selectedProject && (
        <UploadSection>
          <DataUploader
            sessionId={sessionId}
            projectCode={selectedProject}
            onUploadSuccess={handleDataUpload}
            disabled={!selectedProject}
          />
        </UploadSection>
      )}

      <ChatContainer>
        <MessagesArea>
          {messages.map((msg, index) => (
            <MessageRow key={index} type={msg.type}>
              <MessageBubble type={msg.type}>
                {msg.type === 'user' ? (
                  <UserText>{msg.content}</UserText>
                ) : msg.type === 'system' ? (
                  <SystemText>{msg.content}</SystemText>
                ) : msg.type === 'error' ? (
                   <ErrorDisplay>
                     <strong>Error:</strong> {msg.content.error}
                     <p>{msg.content.summary}</p>
                   </ErrorDisplay>
                ) : msg.content?.needsConfirmation ? (
                  <>
                    {msg.content.reasoningSteps && (
                      <ReasoningSteps steps={msg.content.reasoningSteps} />
                    )}
                    <TaxonomyConfirmation
                      alternatives={msg.content.alternatives}
                      message={msg.content.message}
                      confidence={msg.content.confidence}
                      onSelect={(selected) => handleTaxonomySelection(selected, msg.content.originalQuery)}
                      onCancel={handleTaxonomyCancel}
                    />
                  </>
                ) : (
                  <AIContent>
                    {msg.content?.reasoningSteps && (
                      <ReasoningSteps steps={msg.content.reasoningSteps} />
                    )}
                    {msg.content?.reasoningText && (
                      <LogicalThinking text={msg.content.reasoningText} />
                    )}
                    {msg.content?.drillDownSuggestion && (
                      <DrillDownSuggestion 
                        suggestion={msg.content.drillDownSuggestion}
                        onSelectTheme={(theme) => handleDrillDownSelection(theme, msg.content.drillDownSuggestion.category)}
                      />
                    )}
                    <ResultsPanel results={msg.content} />
                    
                    {/* Display all charts if available, otherwise show single chart for backward compatibility */}
                    {msg.content?.chartConfigs && msg.content.chartConfigs.length > 0 ? (
                      msg.content.chartConfigs.map((chartConfig, idx) => (
                        <ChartContainer key={idx}>
                          <ChartHeader>
                            <ChartTitle>{chartConfig.title || `Visualization ${idx + 1}`}</ChartTitle>
                            {idx === 0 && msg.content?.csvData && msg.content.csvData.length > 0 && (
                              <ExportButton onClick={() => handleExportCSV(msg.content.csvData, msg.query)}>
                                📥 Export CSV ({msg.content.csvData.length} rows)
                              </ExportButton>
                            )}
                          </ChartHeader>
                          <ChartRenderer config={chartConfig} />
                        </ChartContainer>
                      ))
                    ) : msg.content?.chartConfig && (
                      <ChartContainer>
                        <ChartHeader>
                          <ChartTitle>{msg.content.chartConfig.title || 'Visualization'}</ChartTitle>
                          {msg.content?.csvData && msg.content.csvData.length > 0 && (
                            <ExportButton onClick={() => handleExportCSV(msg.content.csvData, msg.query)}>
                              📥 Export CSV ({msg.content.csvData.length} rows)
                            </ExportButton>
                          )}
                        </ChartHeader>
                        <ChartRenderer config={msg.content.chartConfig} />
                      </ChartContainer>
                    )}
                    
                    {/* Feedback Buttons */}
                    <FeedbackContainer>
                      <FeedbackButton 
                        onClick={() => handleFeedback(msg.query, msg.content, 'positive')}
                        disabled={msg.feedbackSubmitted}
                        $feedbackType="positive"
                      >
                        👍 Helpful
                      </FeedbackButton>
                      <FeedbackButton 
                        onClick={() => handleFeedback(msg.query, msg.content, 'negative')}
                        disabled={msg.feedbackSubmitted}
                        $feedbackType="negative"
                      >
                        👎 Not Helpful
                      </FeedbackButton>
                      {msg.feedbackSubmitted && (
                        <FeedbackStatus>Thanks for your feedback!</FeedbackStatus>
                      )}
                    </FeedbackContainer>
                  </AIContent>
                )}
              </MessageBubble>
            </MessageRow>
          ))}

          {isProcessing && (
            <MessageRow type="ai">
              <MessageBubble type="ai">
                <ProcessingState>
                  <Spinner />
                  <ProgressInfo>
                    <div className="stage">{progress.stage || 'Thinking...'}</div>
                    <div className="message">{progress.message}</div>
                  </ProgressInfo>
                </ProcessingState>
              </MessageBubble>
            </MessageRow>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>

        <InputArea>
          <InputWrapper>
            <QueryInput
              onSubmit={handleQuery}
              disabled={!selectedProject || isProcessing}
              isProcessing={isProcessing}
              placeholder={selectedProject ? "Ask a question about your data..." : "Select a project first"}
            />
          </InputWrapper>
          <OptionsBar>
            <ToggleLabel>
              <input 
                type="checkbox" 
                checked={isAdvanced} 
                onChange={(e) => setIsAdvanced(e.target.checked)} 
              />
              <span>Advanced Analysis Mode</span>
              <InfoIcon title="Advanced Mode includes detailed statistical breakdowns, confidence intervals, hypothesis testing, and both weighted and unweighted results for comparison.">
                ⓘ
              </InfoIcon>
            </ToggleLabel>
          </OptionsBar>
          <Disclaimer>
            ⚠️ Insights generated using LLM might be inaccurate
          </Disclaimer>
        </InputArea>
      </ChatContainer>
    </Container>
  );
};

export default InsightsAtGPT;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
`;

const ControlsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
`;

const StyledSelect = styled.select`
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #008080;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const UploadSection = styled.div`
  padding: 1rem;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background-color: #f5f5f5;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${props => props.type === 'user' ? 'flex-end' : 'flex-start'};
  width: 100%;
`;

const MessageBubble = styled.div`
  max-width: ${props => props.type === 'user' ? '70%' : '85%'};
  padding: 0.875rem;
  background: ${props => 
    props.type === 'user' ? '#008080' : 
    props.type === 'error' ? '#ffebee' : 'white'};
  color: ${props => props.type === 'user' ? 'white' : '#333'};
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const UserText = styled.div`
  font-size: 1rem;
  line-height: 1.5;
  word-wrap: break-word;
`;

const SystemText = styled.div`
  font-style: italic;
  color: #888;
  font-size: 0.875rem;
  text-align: center;
  padding: 0.5rem;
`;

const AIContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorDisplay = styled.div`
  color: #c62828;
  background: #ffebee;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #c62828;

  strong {
    display: block;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
  }
`;

const ChartContainer = styled.div`
  margin-top: 1rem;
  background: #fafafa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ChartTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #008080;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #006666;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 128, 128, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FeedbackContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const FeedbackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.$feedbackType === 'positive' ? '#f0f9ff' : '#fff5f5'};
  color: ${props => props.$feedbackType === 'positive' ? '#0369a1' : '#b91c1c'};
  border: 1px solid ${props => props.$feedbackType === 'positive' ? '#bae6fd' : '#fecaca'};
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$feedbackType === 'positive' ? '#e0f2fe' : '#fee2e2'};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeedbackStatus = styled.span`
  color: #059669;
  font-size: 0.9rem;
  font-weight: 500;
`;

const InputArea = styled.div`
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const OptionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 0.5rem;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #555;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #008080;
  }

  span {
    font-weight: 500;
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
  color: #008080;
  cursor: help;
  font-weight: bold;
  transition: color 0.2s ease;

  &:hover {
    color: #006666;
  }
`;

const ProcessingState = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #666;
`;

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  .stage {
    font-weight: 600;
    font-size: 0.95rem;
    color: #008080;
  }
  
  .message {
    font-size: 0.85rem;
    color: #777;
  }
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #e8e8e8;
  border-top: 3px solid #008080;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Disclaimer = styled.div`
  font-size: 0.85rem;
  color: #d97706;
  text-align: center;
  padding: 0.5rem 1rem;
  background: #fef3c7;
  border-radius: 4px;
  border: 1px solid #fbbf24;
  margin-top: 0.5rem;
  font-weight: 500;
`;
